import React from 'react';
import { SavedChartState, Measure } from '../types';

interface PrintableChartProps {
  chart: SavedChartState;
}

/**
 * PrintableChart - Version papier optimisée pour l'impression
 * Mise en page inspirée du Gig Mode (grandes polices, layout compact)
 * Couleurs optimisées pour l'impression (fond blanc, texte noir, accents dorés)
 * Format A4 : 4 mesures par ligne
 */
export const PrintableChart: React.FC<PrintableChartProps> = ({ chart }) => {
  const { songTitle, songAuthor, songStyle, keySignature, timeSignature, sourceChart } = chart;

  // Regrouper les mesures par ligne (4 mesures/ligne pour A4)
  const measuresPerRow = 4;
  const rows: Measure[][] = [];
  for (let i = 0; i < sourceChart.length; i += measuresPerRow) {
    rows.push(sourceChart.slice(i, i + measuresPerRow));
  }
  
  // Print-optimized colors (white background, black text, gold accents)
  const PRINT_BG = '#ffffff';
  const PRINT_TEXT = '#000000';
  const PRINT_ACCENT = '#D4A853'; // Gold accent from logo
  const PRINT_GRAY = '#6b7280';
  const MEASURE_BG = '#ffffff';
  const BORDER_COLOR = '#d1d5db';

  // Helper: Calculate incenter position for geometric chord placement
  const getIncenterPosition = (
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    p3: { x: number; y: number }
  ): { top: string; left: string } => {
    const distance = (a: { x: number; y: number }, b: { x: number; y: number }) =>
      Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));

    const a = distance(p2, p3);
    const b = distance(p1, p3);
    const c = distance(p1, p2);

    const perimeter = a + b + c;
    if (perimeter === 0) return { top: '50%', left: '50%' };

    const incenter = {
      x: (a * p1.x + b * p2.x + c * p3.x) / perimeter,
      y: (a * p1.y + b * p2.y + c * p3.y) / perimeter,
    };

    return { top: `${incenter.y}%`, left: `${incenter.x}%` };
  };

  const renderChord = (chord: string | null) => {
    if (!chord) return <span style={{ color: PRINT_GRAY }}>—</span>;
    
    // Simple regex to parse chord: root + accidental + quality/extensions + slash bass
    const chordRegex = /^([A-G])([#b♯♭]?)(.*)$/;
    const match = chord.match(chordRegex);
    
    if (!match) return <span style={{ color: '#ef4444' }}>{chord}</span>;

    const [, root, accidental, rest] = match;
    
    // Check for slash bass notation
    const slashIndex = rest.indexOf('/');
    const hasSlash = slashIndex !== -1 && !rest.includes('6/9');
    const quality = hasSlash ? rest.substring(0, slashIndex) : rest;
    const slashBass = hasSlash ? rest.substring(slashIndex + 1) : null;

    return (
      <span style={{ fontFamily: '"DM Serif Text", serif', color: PRINT_TEXT, display: 'inline-flex', alignItems: 'baseline', fontWeight: 'bold' }}>
        <span style={{ fontSize: '2rem' }}>{root}</span>
        {accidental && (
          <span style={{ fontSize: '1.2rem', marginLeft: '-2px' }}>{accidental}</span>
        )}
        {quality && <span style={{ fontSize: '1.3rem' }}>{quality}</span>}
        {slashBass && (
          <>
            <span style={{ fontSize: '1.3rem', margin: '0 4px' }}>/</span>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{slashBass}</span>
          </>
        )}
      </span>
    );
  };

  // Calculate rows per page (A4 = ~297mm, header ~80mm, footer ~40mm, row ~140px)
  const ROWS_PER_PAGE = 5;
  
  // Group rows into pages - filter out empty pages
  const pages: Measure[][][] = [];
  for (let i = 0; i < rows.length; i += ROWS_PER_PAGE) {
    const pageRows = rows.slice(i, i + ROWS_PER_PAGE);
    // Only add page if it has content
    if (pageRows.length > 0) {
      pages.push(pageRows);
    }
  }

  // Don't render anything if no pages with content
  if (pages.length === 0) {
    return null;
  }

  return (
    <div id="printable-chart">
      {pages.map((pageRows, pageIndex) => (
        <div 
          key={pageIndex}
          style={{ 
            width: '210mm',
            height: '297mm',
            margin: '0 auto',
            backgroundColor: PRINT_BG,
            color: PRINT_TEXT,
            padding: '20px',
            fontFamily: 'sans-serif',
            boxSizing: 'border-box',
            pageBreakAfter: pageIndex < pages.length - 1 ? 'always' : 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header - only on first page */}
          {pageIndex === 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', borderBottom: `2px solid ${PRINT_ACCENT}`, paddingBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '4px', fontFamily: '"DM Serif Text", serif', color: PRINT_TEXT }}>
                  {songTitle || 'Untitled Chart'}
                </h1>
                {songAuthor && (
                  <p style={{ fontSize: '1rem', color: PRINT_GRAY, marginBottom: '4px' }}>by {songAuthor}</p>
                )}
                <div style={{ display: 'flex', gap: '16px', fontSize: '0.875rem', color: PRINT_GRAY, fontWeight: '600' }}>
                  {songStyle && <span>Style: {songStyle}</span>}
                  {keySignature && <span>Key: {keySignature}</span>}
                  {timeSignature && <span>Time: {timeSignature.beats}/{timeSignature.beatType}</span>}
                </div>
              </div>
              
              {/* Logo from PNG file */}
              <img 
                src="/logo%20sans%20fond.png" 
                alt="Chord Notator" 
                style={{ height: '50px', width: 'auto' }}
              />
            </div>
          )}

          {/* Page number for subsequent pages */}
          {pageIndex > 0 && (
            <div style={{ fontSize: '0.75rem', color: PRINT_GRAY, marginBottom: '12px', textAlign: 'right' }}>
              {songTitle} - Page {pageIndex + 1}
            </div>
          )}

          {/* Chart Grid */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
            {pageRows.map((row, localRowIndex) => {
              const globalRowIndex = pageIndex * ROWS_PER_PAGE + localRowIndex;
              
              return (
                <div key={localRowIndex} style={{ display: 'flex', gap: '6px' }}>
                  {row.map((measure, colIndex) => {
                    const measureNumber = globalRowIndex * measuresPerRow + colIndex + 1;
                    const hasRepeatStart = measure.annotations?.startRepeat;
                    const hasRepeatEnd = measure.annotations?.endRepeat;
                    const ending = measure.annotations?.ending;

                    return (
                      <div 
                        key={measure.id} 
                        style={{ flex: 1, minWidth: 0 }}
                      >
                        {/* Measure container */}
                        <div 
                          style={{
                            border: `2px solid ${BORDER_COLOR}`,
                            borderRadius: '4px',
                            backgroundColor: MEASURE_BG,
                            borderLeft: hasRepeatStart ? `4px solid ${PRINT_TEXT}` : undefined,
                            borderRight: hasRepeatEnd ? `4px solid ${PRINT_TEXT}` : undefined,
                            aspectRatio: '1 / 1',
                            position: 'relative',
                            padding: '4px'
                          }}
                        >
                          {/* Header row: measure number + annotations (like Gig Mode) */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '18px', position: 'relative' }}>
                            {/* Measure number - top left like Gig Mode */}
                            <div style={{ fontSize: '10px', color: PRINT_GRAY, fontWeight: 'bold', flexShrink: 0 }}>
                              {measureNumber}
                            </div>
                            
                            {/* Annotations inline after number (like Gig Mode) */}
                            {measure.annotations && (
                              <div style={{ fontSize: '11px', color: PRINT_GRAY, fontWeight: 'bold', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                {measure.annotations.section && <span>[{measure.annotations.section}]</span>}
                                {measure.annotations.rehearsalLetter && <span>({measure.annotations.rehearsalLetter})</span>}
                                
                                {/* Ending number with underline extending to the right */}
                                {ending && (
                                  <span style={{ display: 'flex', alignItems: 'flex-end', flex: 1 }}>
                                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: PRINT_TEXT }}>{ending}.</span>
                                    <span style={{ flex: 1, borderBottom: `1px solid ${PRINT_GRAY}`, marginLeft: '2px', marginBottom: '2px' }}></span>
                                  </span>
                                )}
                                
                                {measure.annotations.tempo && <span>{measure.annotations.tempo}</span>}
                                {measure.annotations.dynamics && <span>{measure.annotations.dynamics}</span>}
                                {measure.annotations.coda && <span>⊕</span>}
                                {measure.annotations.segno && <span>𝄋</span>}
                                {measure.annotations.fine && <span>Fine</span>}
                                {measure.annotations.toCoda && <span>To ⊕</span>}
                                {measure.annotations.dalSegno && <span>D.S.</span>}
                                {measure.annotations.dalCapo && <span>D.C.</span>}
                              </div>
                            )}
                            
                            {/* Ending bracket line if no other annotations but has ending */}
                            {ending && !measure.annotations?.section && !measure.annotations?.rehearsalLetter && (
                              <div style={{ display: 'flex', alignItems: 'flex-end', flex: 1 }}>
                                <span style={{ fontSize: '12px', fontWeight: 'bold', color: PRINT_TEXT }}>{ending}.</span>
                                <span style={{ flex: 1, borderBottom: `1px solid ${PRINT_GRAY}`, marginLeft: '2px', marginBottom: '2px' }}></span>
                              </div>
                            )}
                          </div>

                          {/* Chords - Gig Mode geometric positioning */}
                          <div style={{ position: 'absolute', top: '22px', left: '4px', right: '4px', bottom: measure.lyrics ? '24px' : '4px' }}>
                      {(() => {
                        const chords = measure.chords;
                        const subdivision = chords.length;
                        const centerTransform = 'translate(-50%, -50%)';

                        // Dividers
                        const XDivider = (
                          <svg 
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', color: BORDER_COLOR }} 
                            stroke="currentColor" 
                            strokeWidth="1"
                          >
                            <line x1="0" y1="0" x2="100%" y2="100%" />
                            <line x1="100%" y1="0" x2="0" y2="100%" />
                          </svg>
                        );

                        const DiagonalDivider = (
                          <svg 
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', color: BORDER_COLOR }} 
                            stroke="currentColor" 
                            strokeWidth="1"
                          >
                            <line x1="0" y1="100%" x2="100%" y2="0" />
                          </svg>
                        );

                        if (subdivision === 1) {
                          return (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {renderChord(chords[0])}
                            </div>
                          );
                        }

                        if (subdivision === 2) {
                          const pos1 = getIncenterPosition({ x: 0, y: 0 }, { x: 100, y: 0 }, { x: 0, y: 100 });
                          const pos2 = getIncenterPosition({ x: 100, y: 0 }, { x: 100, y: 100 }, { x: 0, y: 100 });
                          
                          return (
                            <>
                              {DiagonalDivider}
                              <div style={{ position: 'absolute', ...pos1, transform: centerTransform }}>
                                {renderChord(chords[0])}
                              </div>
                              <div style={{ position: 'absolute', ...pos2, transform: centerTransform }}>
                                {renderChord(chords[1])}
                              </div>
                            </>
                          );
                        }

                        if (subdivision === 3) {
                          return (
                            <>
                              {XDivider}
                              <div style={{ position: 'absolute', top: '25%', left: '25%', transform: centerTransform }}>
                                {renderChord(chords[0])}
                              </div>
                              <div style={{ position: 'absolute', top: '25%', left: '75%', transform: centerTransform }}>
                                {renderChord(chords[1])}
                              </div>
                              <div style={{ position: 'absolute', top: '75%', left: '50%', transform: centerTransform }}>
                                {renderChord(chords[2])}
                              </div>
                            </>
                          );
                        }

                        if (subdivision === 4) {
                          const center = { x: 50, y: 50 };
                          const tl = { x: 0, y: 0 };
                          const tr = { x: 100, y: 0 };
                          const bl = { x: 0, y: 100 };
                          const br = { x: 100, y: 100 };

                          const pos1 = getIncenterPosition(tl, bl, center);
                          const pos2 = getIncenterPosition(tl, tr, center);
                          const pos3 = getIncenterPosition(bl, br, center);
                          const pos4 = getIncenterPosition(tr, br, center);

                          return (
                            <>
                              {XDivider}
                              <div style={{ position: 'absolute', ...pos1, transform: centerTransform }}>
                                {renderChord(chords[0])}
                              </div>
                              <div style={{ position: 'absolute', ...pos2, transform: centerTransform }}>
                                {renderChord(chords[1])}
                              </div>
                              <div style={{ position: 'absolute', ...pos3, transform: centerTransform }}>
                                {renderChord(chords[2])}
                              </div>
                              <div style={{ position: 'absolute', ...pos4, transform: centerTransform }}>
                                {renderChord(chords[3])}
                              </div>
                            </>
                          );
                        }

                        return null;
                      })()}
                    </div>

                          {/* Lyrics */}
                          {measure.lyrics && (
                            <div style={{ position: 'absolute', bottom: '4px', left: '4px', right: '4px', fontSize: '11px', color: PRINT_TEXT, textAlign: 'center', borderTop: `1px solid ${BORDER_COLOR}`, paddingTop: '2px' }}>
                              {measure.lyrics}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Fill remaining cells if row is incomplete */}
                  {row.length < measuresPerRow && 
                    Array.from({ length: measuresPerRow - row.length }).map((_, i) => (
                      <div key={`empty-${i}`} style={{ flex: 1, minWidth: 0 }} />
                    ))
                  }
                </div>
              );
            })}
          </div>

          {/* Footer - only on last page */}
          {pageIndex === pages.length - 1 && (
            <div style={{ marginTop: 'auto', paddingTop: '12px', borderTop: `2px solid ${PRINT_ACCENT}`, fontSize: '0.75rem', color: PRINT_GRAY, textAlign: 'center' }}>
              Created with Chord Notator · chordnotator.com
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
